import { BillingOverrides, DurPpsOverride } from "@/models/BillingOverrides";
import { EligibilityClarificationCode, OtherCoverageCode } from "@/models/PatientPlan";

export enum ScheduledClaimExpectedOutcome {
    Paid = 0,
    Rejected = 1,
}

export enum ScheduledClaimStatus {
    Pending = 0,
    Processing = 1,
    Completed = 2,
    Cancelled = 3,
    Failed = 4,
}

export interface ScheduledClaimDurOverride {
    id?: number | null;
    scheduledClaimId?: number;
    sequenceNumber: number;
    reasonForServiceCode: string | null;
    professionalServiceCode: string | null;
    resultOfServiceCode: string | null;
    levelOfEffort: string | null;
}

/**
 * A billing decision staged against a fill whose fill date has not arrived. The scheduled claim
 * worker walks a fill's claims by ascending sequencePosition and stops at the first plan that
 * produces the expected outcome, so position is what decides which plan is billed first.
 */
export class ScheduledClaim {
    id: number | null = null;
    storeID: number = 0;
    rxNumber: number = 0;
    rfNumber: number = 0;
    patientPlanID: number = 0;
    /** Zero means "append to the end of the fill's sequence"; the save proc assigns the real one. */
    sequencePosition: number = 0;
    expectedOutcome: ScheduledClaimExpectedOutcome = ScheduledClaimExpectedOutcome.Paid;
    /** Comma-delimited NCPDP reject codes that must come back for a rejection to count as expected. */
    expectedRejectCodes: string | null = null;

    priorAuthTypeCode: string | null = null;
    priorAuthNumber: string | null = null;
    submissionClarificationCode: number | null = null;
    eligibilityClarificationCode: EligibilityClarificationCode | null = null;
    scheduledPrescriptionIdNumber: string | null = null;
    usualAndCustomaryOverride: number | null = null;
    incentiveAmountOverride: number | null = null;
    daysSupplyOverride: number | null = null;
    otherCoverageCodeOverride: OtherCoverageCode | null = null;
    taxPercentageOverride: number | null = null;
    icd10: string | null = null;
    dateOfInjuryOverride: string | null = null;
    rejectCodeOverride: string | null = null;

    status: ScheduledClaimStatus = ScheduledClaimStatus.Pending;
    scheduled: string | null = null;
    scheduledBy: string | null = null;
    processed: string | null = null;
    claimId: number | null = null;
    updated: string | null = null;
    updatedBy: string | null = null;

    durOverrides: ScheduledClaimDurOverride[] = [];

    constructor(source: Partial<ScheduledClaim> | null = null) {
        if (source) Object.assign(this, source);
        if (!Array.isArray(this.durOverrides)) this.durOverrides = [];
    }

    /** Only a claim the worker has not started can still be edited, reordered, or cancelled. */
    get isPending(): boolean {
        return this.status === ScheduledClaimStatus.Pending;
    }

    /**
     * Whether this claim still stands between its plan and a new one being staged for it.
     *
     * Wider than isPending, and deliberately so: a Processing claim can no longer be edited, but the
     * worker has leased it and is about to transmit it, so its plan is still spoken for. This is the
     * predicate usp_ScheduledClaim_Save's duplicate guard uses (`Status IN (0, 1)`) — offering a plan
     * the proc will refuse only moves the refusal from the select box to an error dialog. Completed
     * and Failed are history and say nothing about what is still going to be transmitted.
     */
    get isLive(): boolean {
        return this.status === ScheduledClaimStatus.Pending || this.status === ScheduledClaimStatus.Processing;
    }

    get expectsRejection(): boolean {
        return this.expectedOutcome === ScheduledClaimExpectedOutcome.Rejected;
    }

    /** The staged reject codes as a trimmed list, which is how the sequence panel offers them. */
    get expectedRejectCodeList(): string[] {
        return (this.expectedRejectCodes ?? "")
            .split(",")
            .map(code => code.trim())
            .filter(code => code.length > 0);
    }

    /**
     * The staged overrides in the shape the shared override form binds to. The two models name
     * several of the same NCPDP fields differently — the form carries the billing endpoint's
     * parameter names (paOverride, tsn), the staged row carries the NCPDP field names — so the
     * translation lives here rather than in either form.
     */
    toOverrides(): BillingOverrides {
        return new BillingOverrides({
            paTypeCode: this.priorAuthTypeCode ?? "",
            paOverride: this.priorAuthNumber ?? "",
            tsn: this.scheduledPrescriptionIdNumber ?? "",
            submissionClarificationCode: this.submissionClarificationCode?.toString() ?? "",
            eligibilityClarificationCodeOverride: this.eligibilityClarificationCode,
            uacOverride: this.usualAndCustomaryOverride?.toString() ?? "",
            incentiveAmountOverride: this.incentiveAmountOverride?.toString() ?? "",
            icd10: this.icd10 ?? "",
            dateOfInjuryOverride: this.dateOfInjuryOverride ?? "",
            otherCoverageCodeOverride: this.otherCoverageCodeOverride,
            taxPercentageOverride: this.taxPercentageOverride,
            rejectCodeOverride: this.rejectCodeOverride,
            daysSupplyOverride: this.daysSupplyOverride,
            durPpsOverrides: this.durOverrides.map(d => ({
                reasonForServiceCode: d.reasonForServiceCode ?? "",
                professionalServiceCode: d.professionalServiceCode ?? "",
                resultOfServiceCode: d.resultOfServiceCode ?? "",
                levelOfEffort: d.levelOfEffort ?? "",
            })),
        });
    }

    /**
     * Copies an edited override set back onto the staged row. Empty is stored as null rather than
     * as an empty string so a cleared box reads the same as one that was never filled in — the
     * billing pipeline treats null as "no override" and "" as a value it must try to parse.
     */
    applyOverrides(overrides: BillingOverrides): void {
        this.priorAuthTypeCode = blankToNull(overrides.paTypeCode);
        this.priorAuthNumber = blankToNull(overrides.paOverride);
        this.scheduledPrescriptionIdNumber = blankToNull(overrides.tsn);
        this.submissionClarificationCode = blankToNumber(overrides.submissionClarificationCode);
        this.eligibilityClarificationCode = overrides.eligibilityClarificationCodeOverride ?? null;
        this.usualAndCustomaryOverride = blankToNumber(overrides.uacOverride);
        this.incentiveAmountOverride = blankToNumber(overrides.incentiveAmountOverride);
        this.icd10 = blankToNull(overrides.icd10);
        this.dateOfInjuryOverride = blankToNull(overrides.dateOfInjuryOverride);
        this.otherCoverageCodeOverride = overrides.otherCoverageCodeOverride ?? null;
        this.taxPercentageOverride = blankToNumber(overrides.taxPercentageOverride);
        this.rejectCodeOverride = blankToNull(overrides.rejectCodeOverride);
        this.daysSupplyOverride = blankToNumber(overrides.daysSupplyOverride);

        // Positions are assigned from the row order the technician sees, so the staged segment
        // transmits in the order it was entered rather than in whatever order the rows persisted in.
        this.durOverrides = overrides.durPpsOverrides.map((d: DurPpsOverride, index: number) => ({
            sequenceNumber: index + 1,
            reasonForServiceCode: blankToNull(d.reasonForServiceCode),
            professionalServiceCode: blankToNull(d.professionalServiceCode),
            resultOfServiceCode: blankToNull(d.resultOfServiceCode),
            levelOfEffort: blankToNull(d.levelOfEffort),
        }));
    }
}

/**
 * Both helpers take what the override controls actually produce rather than what the field they
 * feed is declared as, because the two do not match.
 *
 * EnumSelector emits the numeric enum member for every code it offers, and the `.number` modifier
 * on the numeric boxes leaves the original string in place when one is cleared. Assuming a string
 * here threw on the first code a technician selected, which killed the save before it reached the
 * server — and did it silently, because nothing on the way out reported the throw.
 */
function blankToNull(value: unknown): string | null {
    const trimmed = value == null ? "" : String(value).trim();
    return trimmed.length > 0 ? trimmed : null;
}

function blankToNumber(value: unknown): number | null {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const trimmed = blankToNull(value);
    if (trimmed === null) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
}
